import { Controller } from '@hotwired/stimulus';
import Rails from "@rails/ujs";

export default class extends Controller {
  static targets = [ 'checkbox', 'query', 'clearButton' ];

  connect(){
    this.boundUpdateURL = this.updateURL.bind(this);
    this.element.addEventListener('turbo:submit-end', this.boundUpdateURL);
    this.processFilterCountMutation(this.element);
  }

  submit() {
    // Filter changes and an explicit query submit both come through here. The
    // cold modal has no #search_results; the server skips that render (embedded=1).
    Rails.fire(this.element, 'submit');
  }

  // The query is not searched on each keystroke. Enter calls this; Show
  // results does too when the box has uncommitted text.
  submitQuery() {
    this.prepareQuerySubmit();
    this.submit();
  }

  prepareQuerySubmit() {
    this.resetOrderForQueryChange();
    this.rememberQuery();
  }

  queryIsDirty() {
    if (!this.hasQueryTarget) return false;

    return this.queryTarget.value !== this.committedQuery;
  }

  rememberQuery() {
    this.committedQuery = this.hasQueryTarget ? this.queryTarget.value : '';
  }

  // The cold modal (off the results page) has no #search_results region.
  get isCold() {
    return !document.getElementById('search_results');
  }

  clearQuery() {
    this.queryTarget.value = '';
    this.toggleClearButton();
    this.resetOrderForQueryChange();
    this.rememberQuery();
    this.submit();
    setTimeout(() => {
      this.queryTarget.focus();
    }, 50);
  }

  // Changing the search term should return to relevance ranking. An earlier
  // Most Recent / Liked / Viewed choice stays checked in the form radios and
  // would otherwise keep overriding the server's query-aware default.
  // Leave radios unchecked (so order isn't submitted) but mark the effective
  // default with .is-default — turbo streams don't re-render the sort row.
  resetOrderForQueryChange() {
    const queryPresent = this.hasQueryTarget && this.queryTarget.value.trim() !== '';
    const defaultOrder = queryPresent ? 'most_relevant' : 'most_recent';

    this.element.querySelectorAll('input[name="order"]').forEach((radio) => {
      radio.checked = false;
      radio.closest('label')?.classList.toggle('is-default', radio.value === defaultOrder);
    });
  }

  toggleClearButton() {
    if (this.hasClearButtonTarget) {
      if (this.queryTarget.value.trim() === '') {
        this.clearButtonTarget.classList.add('hidden');
      } else {
        this.clearButtonTarget.classList.remove('hidden');
      }
    }
  }

  updateURL(event) {
    // No live submits happen in the cold modal; let Turbo own history there.
    // An aborted submit (Enter, then Clear all before the response) has no
    // fetchResponse — nothing to record, and reading it would throw.
    if (this.isCold) return;
    const url = event.detail?.fetchResponse?.response?.url;
    if (!url) return;
    // Inside an open mobile filter modal each submit is exploratory: replace the
    // history entry so cancel/back land on the committed URL, not a half-built
    // query (modal_filters_controller sets this flag while the modal is open).
    const method = this.element.dataset.modalSession ? 'replaceState' : 'pushState';
    window.history[method](window.history.state, null, url);
  }

  queryTargetConnected(target) {
    if (this.committedQuery === undefined) this.rememberQuery();

    // Typing only reveals the clear control. The search waits for Enter so a
    // mistype doesn't hit the reranker.
    target.addEventListener('input', () => {
      this.toggleClearButton();
    });

    // Enter/"Go" on the (mobile) keyboard signals the user is done typing: run
    // the search immediately and blur the input so the keyboard drops, revealing
    // the results and the "Show results" button. Without the blur the keyboard
    // stays up covering most of the modal, and nothing visibly happens on Enter.
    // preventDefault stops the native form submit that would double-fire here.
    target.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.submitQuery();
        target.blur();
      }
    });
  }

  checkboxTargetConnected(target) {
    target.addEventListener('change', () => {
      this.submit();
    });
  }

  processFilterCountMutation(element) {
    const config = { childList: true, subtree: true, characterData: true };

    // Callback function to execute when mutations are observed
    const callback = (mutationList, observer) => {
      for (const mutation of mutationList) {
        if ( mutation.target.classList.contains('filter-count') ){
          const activeFilterCount = parseInt(mutation.target.innerHTML);
          if ( activeFilterCount > 0){
            mutation.target.classList.remove('hidden');
          } else {
            mutation.target.classList.add('hidden');
          }
        }
      }
    };

    // Create an observer instance linked to the callback function
    const observer = new MutationObserver(callback);

    // Start observing the target node for configured mutations
    observer.observe(element, config);

    // Later, you can stop observing
    // observer.disconnect();
  }
}
